<div class="gsc-dashboard">

  <!-- Not connected state -->
  <div *ngIf="!isConnected && !authorizationRequired" class="gsc-not-connected">
    <div class="gsc-not-connected-inner">
      <i class="pi pi-search gsc-not-connected-icon"></i>
      <h3 class="gsc-not-connected-title">Google Search Console not connected</h3>
      <p class="gsc-not-connected-body">
        Connect Google Search Console in <strong>Settings &rsaquo; Projects &rsaquo; Integrations</strong> to see your search performance data here.
      </p>
    </div>
  </div>

  <div *ngIf="authorizationRequired" class="gsc-error" role="alert">
    <i class="pi pi-lock gsc-error-icon"></i>
    <h3 class="gsc-not-connected-title">Google authorization expired</h3>
    <p>Your saved Search Console configuration is still here. Reconnect Google to resume importing data.</p>
    <a class="p-button p-button-sm" routerLink="/settings/manage/projects">Reconnect Google</a>
  </div>

  <!-- Loading state -->
  <div *ngIf="isConnected && isLoading" class="gsc-loading">
    <i class="pi pi-spin pi-spinner gsc-loading-icon"></i>
    <p>Loading Search Console data&hellip;</p>
  </div>

  <!-- Error state -->
  <div *ngIf="isConnected && !isLoading && hasError && !authorizationRequired" class="gsc-error">
    <i class="pi pi-exclamation-triangle gsc-error-icon"></i>
    <p>Unable to load Search Console data. Please check your connection and try again.</p>
  </div>

  <!-- KPI grid -->
  <div *ngIf="isConnected && !isLoading && !hasError && summary" class="gsc-kpi-grid">

    <!-- Clicks -->
    <div class="gsc-kpi-card">
      <div class="gsc-kpi-header">
        <span class="gsc-kpi-title">Clicks</span>
        <i class="pi pi-info-circle gsc-kpi-tooltip-icon"
           [pTooltip]="tooltips.clicks"
           tooltipPosition="top"></i>
      </div>
      <div class="gsc-kpi-value">{{ summary.clicks.current | number: '1.0-0' }}</div>
      <div class="gsc-kpi-delta"
           [class.gsc-delta-positive]="isDeltaPositive('clicks', summary.clicks.delta)"
           [class.gsc-delta-negative]="!isDeltaPositive('clicks', summary.clicks.delta)">
        <i class="pi" [class.pi-arrow-up]="summary.clicks.delta >= 0" [class.pi-arrow-down]="summary.clicks.delta < 0"></i>
        {{ formatDelta('clicks', summary.clicks) }}
        <span class="gsc-delta-label">vs prior 28d</span>
      </div>
      <div class="gsc-sparkline">
        <p-chart type="line"
                 [data]="buildSparklineChart(summary.clicks.sparklineValues, '#873EFE')"
                 [options]="sparklineOptions"
                 height="60">
        </p-chart>
      </div>
    </div>

    <!-- Impressions -->
    <div class="gsc-kpi-card">
      <div class="gsc-kpi-header">
        <span class="gsc-kpi-title">Impressions</span>
        <i class="pi pi-info-circle gsc-kpi-tooltip-icon"
           [pTooltip]="tooltips.impressions"
           tooltipPosition="top"></i>
      </div>
      <div class="gsc-kpi-value">{{ summary.impressions.current | number: '1.0-0' }}</div>
      <div class="gsc-kpi-delta"
           [class.gsc-delta-positive]="isDeltaPositive('impressions', summary.impressions.delta)"
           [class.gsc-delta-negative]="!isDeltaPositive('impressions', summary.impressions.delta)">
        <i class="pi" [class.pi-arrow-up]="summary.impressions.delta >= 0" [class.pi-arrow-down]="summary.impressions.delta < 0"></i>
        {{ formatDelta('impressions', summary.impressions) }}
        <span class="gsc-delta-label">vs prior 28d</span>
      </div>
      <div class="gsc-sparkline">
        <p-chart type="line"
                 [data]="buildSparklineChart(summary.impressions.sparklineValues, '#42A5F5')"
                 [options]="sparklineOptions"
                 height="60">
        </p-chart>
      </div>
    </div>

    <!-- CTR -->
    <div class="gsc-kpi-card">
      <div class="gsc-kpi-header">
        <span class="gsc-kpi-title">CTR</span>
        <i class="pi pi-info-circle gsc-kpi-tooltip-icon"
           [pTooltip]="tooltips.ctr"
           tooltipPosition="top"></i>
      </div>
      <div class="gsc-kpi-value">{{ formatCtr(summary.ctr.current) }}</div>
      <div class="gsc-kpi-delta"
           [class.gsc-delta-positive]="isDeltaPositive('ctr', summary.ctr.delta)"
           [class.gsc-delta-negative]="!isDeltaPositive('ctr', summary.ctr.delta)">
        <i class="pi" [class.pi-arrow-up]="summary.ctr.delta >= 0" [class.pi-arrow-down]="summary.ctr.delta < 0"></i>
        {{ formatDelta('ctr', summary.ctr) }}
        <span class="gsc-delta-label">vs prior 28d</span>
      </div>
      <div class="gsc-sparkline">
        <p-chart type="line"
                 [data]="buildSparklineChart(summary.ctr.sparklineValues, '#66BB6A')"
                 [options]="sparklineOptions"
                 height="60">
        </p-chart>
      </div>
    </div>

    <!-- Average Position -->
    <div class="gsc-kpi-card">
      <div class="gsc-kpi-header">
        <span class="gsc-kpi-title">Avg. Position</span>
        <i class="pi pi-info-circle gsc-kpi-tooltip-icon"
           [pTooltip]="tooltips.avgPosition"
           tooltipPosition="top"></i>
      </div>
      <div class="gsc-kpi-value">{{ formatPosition(summary.avgPosition.current) }}</div>
      <div class="gsc-kpi-delta"
           [class.gsc-delta-positive]="isDeltaPositive('avgPosition', summary.avgPosition.delta)"
           [class.gsc-delta-negative]="!isDeltaPositive('avgPosition', summary.avgPosition.delta)">
        <!-- Position: arrow-down = rank improved (lower number) -->
        <i class="pi" [class.pi-arrow-down]="summary.avgPosition.delta < 0" [class.pi-arrow-up]="summary.avgPosition.delta >= 0"></i>
        {{ formatDelta('avgPosition', summary.avgPosition) }}
        <span class="gsc-delta-label">vs prior 28d</span>
      </div>
      <div class="gsc-sparkline">
        <p-chart type="line"
                 [data]="buildSparklineChart(summary.avgPosition.sparklineValues, '#FFA726')"
                 [options]="sparklineOptions"
                 height="60">
        </p-chart>
      </div>
    </div>

  </div>

</div>
